// System boards in the web's navigation (TOM-358, boards-v3.md "Your boards and
// the sidebar", "System boards"; cloud ADR 0305 §5, ADR 0306 §3): which one is
// the boat's mode board, where `instruments` and `live` go, and each board's
// icon. Pure.

import {
  OVERVIEW_MODE_KEYS,
  type NeoBoardSummary,
  type NeoSystemBoardKey,
  type OverviewArchetype,
} from '@neomaris/protocol';

/**
 * The board for the bar's mode slot (ADR 0306 §3): At anchor while the boat is
 * anchored or an anchor watch is on, Underway while underway, At dock while
 * docked, and none while the mode is unknown.
 */
export function modeBoardKey(
  archetype: OverviewArchetype | null,
  anchorWatchOn: boolean
): OverviewArchetype | null {
  if (anchorWatchOn) return 'anchored';
  return archetype;
}

/** Where `instruments` and `live` go: the mode's board, At anchor only while it is unknown. */
export function redirectKey(archetype: OverviewArchetype | null): OverviewArchetype {
  return archetype ?? 'anchored';
}

/** A system board by its key, never by its slug (ADR 0305 §2). */
export function systemBoardFor<B extends Pick<NeoBoardSummary, 'systemKey'>>(
  boards: readonly B[],
  key: NeoSystemBoardKey | null
): B | null {
  if (key === null) return null;
  return boards.find((b) => b.systemKey === key) ?? null;
}

/** True for one of the three mode boards. */
export function isModeBoardKey(key: string | null | undefined): key is OverviewArchetype {
  return (OVERVIEW_MODE_KEYS as readonly string[]).includes(key ?? '');
}

const SYSTEM_BOARD_ICONS: Readonly<Record<NeoSystemBoardKey, string>> = {
  anchored: 'anchor',
  underway: 'underway',
  docked: 'docked',
  wind: 'wind',
  depth: 'water',
  engines: 'engine',
  energy: 'battery',
  tanks: 'fuel',
  climate: 'sun',
  trends: 'activity',
};

/** A board's icon in the sidebar, the bar and Your boards (boards-v3.md). */
export function boardIcon(board: Pick<NeoBoardSummary, 'systemKey'>): string {
  return board.systemKey ? SYSTEM_BOARD_ICONS[board.systemKey] : 'boards';
}
